import React, { useState, useEffect } from 'react';
import { ingestionAPI, judgeAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function RSSIngestion() {
  const [loading, setLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [stats, setStats] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    loadScanHistory();
  }, []);

  const loadScanHistory = async () => {
    setLoadingHistory(true);
    try {
      // Fetch scored articles and group by date to create scan history
      const response = await judgeAPI.getScoredArticles();
      const articles = response.data?.data || [];
      
      // Group articles by date
      const dateGroups = {};
      const sourcesByDate = {};
      
      articles.forEach(article => {
        const timelineDate = article.published_at || article.scored_at;
        if (timelineDate) {
          const date = new Date(timelineDate);
          const dateKey = date.toISOString().split('T')[0];
          
          if (!dateGroups[dateKey]) {
            dateGroups[dateKey] = {
              date: date,
              articlesScraped: 0,
              sources: new Set(),
              status: 'success'
            };
          }
          
          dateGroups[dateKey].articlesScraped++;
          if (article.source_name) {
            dateGroups[dateKey].sources.add(article.source_name);
          }
        }
      });
      
      // Convert to array and format
      const history = Object.values(dateGroups)
        .map(group => ({
          date: group.date,
          articlesScraped: group.articlesScraped,
          sources: group.sources.size,
          status: group.sources.size >= 3 ? 'success' : 'partial'
        }))
        .sort((a, b) => b.date - a.date)
        .slice(0, 10); // Show last 10 scans
      
      setScanHistory(history);
    } catch (error) {
      console.error('Failed to load scan history:', error);
      toast.error('Failed to load scan history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const triggerIngestion = async () => {
    setLoading(true);
    const toastId = toast.loading('Triggering RSS ingestion...');
    
    try {
      const response = await ingestionAPI.triggerRSS();
      setLastRun(new Date());
      setStats(response.data);
      toast.success('RSS ingestion triggered successfully!', { id: toastId });
      // Reload history after successful scan
      await loadScanHistory();
    } catch (error) {
      toast.error(`Failed to trigger ingestion: ${error.response?.data?.message || error.message}`, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Modern Header */}
      <div className="relative bg-gradient-to-r from-orange-600 via-red-700 to-pink-800 rounded-3xl shadow-2xl p-8 mb-8 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7m-6 0a1 1 0 11-2 0 1 1 0 012 0z" />
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-1">RSS Ingestion 📡</h2>
              <p className="text-white/90">
                Collect latest news articles from configured RSS feeds
              </p>
            </div>
          </div>
          <button
            onClick={triggerIngestion}
            disabled={loading}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-6 w-6 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Trigger RSS Scan
              </>
            )}
          </button>
        </div>
      </div>

      {/* Last Run Info */}
      {lastRun && (
        <div className="bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border-2 border-green-200 rounded-2xl p-6 mb-8 shadow-lg animate-fade-in">
          <div className="flex items-start">
            <div className="bg-green-600 rounded-xl p-3 mr-4">
              <svg className="h-6 w-6 text-white" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-1">✅ Last Scan Completed</h3>
              <p className="text-gray-700">{lastRun.toLocaleString()}</p>
              {stats && (
                <p className="mt-2 text-green-700 font-semibold">
                  📰 {stats.articles_fetched || 0} articles fetched
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scan History Table */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 mb-8">
        <div className="bg-gradient-to-r from-gray-50 via-orange-50 to-red-50 px-6 py-4 border-b-2 border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <svg className="h-6 w-6 mr-2 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            📊 Scan History
          </h3>
        </div>
        <div className="overflow-x-auto">
          {loadingHistory ? (
            <div className="flex justify-center items-center p-12">
              <div className="relative">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-600"></div>
                <div className="absolute top-0 left-0 h-12 w-12 rounded-full bg-orange-100"></div>
              </div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Articles Scraped</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Sources</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scanHistory.map((scan, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{scan.date.toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">{scan.date.toLocaleTimeString()}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-bold text-gray-900">{scan.articlesScraped}</div>
                      <div className="text-xs text-gray-500">articles</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                        {scan.sources} sources
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {scan.status === 'success' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Success
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
                          <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Partial
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* RSS Sources */}
      <div className="bg-white shadow-xl rounded-2xl p-6 border border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="h-6 w-6 mr-2 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 5c7.18 0 13 5.82 13 13M6 11a7 7 0 017 7m-6 0a1 1 0 11-2 0 1 1 0 012 0z" />
          </svg>
          🌐 Configured RSS Sources
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { name: 'The Hindu', url: 'https://www.thehindu.com/news/national/feeder/default.rss', category: 'National', gradient: 'from-blue-500 to-indigo-600' },
            { name: 'Indian Express', url: 'https://indianexpress.com/section/india/feed/', category: 'India', gradient: 'from-purple-500 to-pink-600' },
            { name: 'Times of India', url: 'https://timesofindia.indiatimes.com/rssfeeds/-2128936835.cms', category: 'India', gradient: 'from-orange-500 to-red-600' },
            { name: 'BBC India', url: 'http://feeds.bbci.co.uk/news/world/asia/india/rss.xml', category: 'International', gradient: 'from-green-500 to-teal-600' },
          ].map((source) => (
            <div key={source.name} className="group bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 hover:border-orange-300 rounded-xl p-4 transition-all hover:shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-sm font-bold text-gray-900 mb-1">{source.name}</div>
                  <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r ${source.gradient} text-white shadow-sm`}>
                    {source.category}
                  </div>
                </div>
                <div className="relative">
                  <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 h-3 w-3 bg-green-400 rounded-full animate-ping"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
